// Customer accounts: /account/login (sign in by password or email code, sign up verified by an email code,
// password reset) and the /account hub (orders, addresses, profile, security). The server decides everything;
// this file only moves the visitor between steps. Server: darat/storefront/accounts.py + api.account_*.
import { $, $$, api, store, t, toast, icon } from "./util";
import { cart } from "./cart";
import { loadCities } from "./address";

const PREFILL = "darat_signup_prefill";  // sessionStorage: set by the order page's "create an account" offer

// ---------------------------------------------------------------- shared bits
function showErr(form, msg) {
	const el = $("[data-err]", form);
	if (!el) return;
	el.textContent = msg || "";
	el.hidden = !msg;
}
function busy(btn, on) {
	if (!btn) return;
	btn.disabled = on;
	btn.classList.toggle("is-loading", on);
}
function eyes(root) {
	root.addEventListener("click", (e) => {
		const b = e.target.closest("[data-eye]");
		if (!b) return;
		const input = b.parentElement.querySelector("input");
		input.type = input.type === "password" ? "text" : "password";
		b.setAttribute("aria-pressed", input.type === "text");
	});
}
// rough strength for the meter; the server enforces the real policy (length, letters+digits, zxcvbn, breaches)
function strength(pw) {
	let s = 0;
	if (pw.length >= 8) s++;
	if (/[A-Za-z؀-ۿ]/.test(pw) && /\d/.test(pw)) s++;
	if (pw.length >= 12) s++;
	if (/[^A-Za-z0-9؀-ۿ]/.test(pw) || (/[a-z]/.test(pw) && /[A-Z]/.test(pw))) s++;
	return pw.length < 8 ? Math.min(s, 1) : s;
}
function meters(root) {
	root.addEventListener("input", (e) => {
		// password: strength bar + live checklist (in the field or the block right after it)
		const input = e.target.closest("[data-strength]");
		if (input) {
			const field = input.closest("label");
			const box = field.nextElementSibling && field.nextElementSibling.matches("[data-pwcheck]") ? field.nextElementSibling : field;
			const v = input.value, m = $("[data-meter]", box);
			if (m) m.dataset.s = v ? strength(v) : 0;
			const rules = { len: v.length >= 8, mix: /[A-Za-z\u0600-\u06FF]/.test(v) && /\d/.test(v), long: v.length >= 12 || /[^A-Za-z0-9\u0600-\u06FF]/.test(v) };
			$$("[data-rule]", box).forEach((li) => li.classList.toggle("is-ok", !!rules[li.dataset.rule]));
		}
		// email / mobile: a check mark once the value looks right
		const chk = e.target.closest("[data-check]");
		if (chk) {
			const v = chk.value.trim();
			const ok = chk.dataset.check === "email" ? /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)
				: /^(\+?966|0)?5\d{8}$/.test(v.replace(/[\s-]/g, "").replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d)));
			chk.closest("label").classList.toggle("is-valid", ok);
		}
	});
	// Caps Lock warning while typing a password
	const caps = (e) => {
		const input = e.target.closest && e.target.closest("[data-caps]");
		if (!input || !e.getModifierState) return;
		const warn = $("[data-caps-warn]", input.closest("form"));
		if (warn) warn.hidden = !e.getModifierState("CapsLock");
	};
	root.addEventListener("keydown", caps);
	root.addEventListener("keyup", caps);
}

// after any successful sign-in: bring this browser's wishlist / saved laptop into the account
async function syncDevice() {
	try {
		const res = await api("darat.storefront.api.account_sync", { wish: store("darat_wish") || [], laptop: store("darat_mylaptop") }, "POST");
		if (res) {
			store("darat_wish", res.wish || []);
			if (res.laptop) store("darat_mylaptop", res.laptop);
		}
	} catch (e) { /* optional */ }
}
function guestTokens() {
	const out = [];
	const paying = store("darat_paying");
	if (paying && paying.token) out.push(paying.token);
	try { const p = JSON.parse(sessionStorage.getItem(PREFILL) || "null"); if (p && p.token) out.push(p.token); } catch (e) { /* private mode */ }
	return out;
}

// ---------------------------------------------------------------- /account/login
function initAuth() {
	const root = $("[data-auth]");
	if (!root) return;
	const ft = root.dataset.ft;
	const after = root.dataset.redirect || "";
	const panes = { signin: $("[data-pane=signin]", root), signup: $("[data-pane=signup]", root), code: $("[data-pane=code]", root) };
	const seg = $("[data-tabs-auth]", root);
	let flow = null;  // code step: {mode: signup|reset|staff, email, data}

	const show = (name) => {
		Object.entries(panes).forEach(([k, el]) => (el.hidden = k !== name));
		if (name !== "code") {
			$$("[data-auth-tab]", seg).forEach((b) => b.setAttribute("aria-selected", b.dataset.authTab === name));
			seg.dataset.on = name;
		}
		seg.hidden = name === "code";
		const first = panes[name].querySelector("input:not(.ds-hp):not([type=hidden])");
		if (first && window.matchMedia("(pointer: fine)").matches) first.focus();
	};
	root.addEventListener("click", (e) => {
		const tab = e.target.closest("[data-auth-tab]");
		if (tab) { show(tab.dataset.authTab); history.replaceState(null, "", tab.dataset.authTab === "signup" ? "#signup" : location.pathname + location.search); }
		if (e.target.closest("[data-back]")) show(flow && flow.mode === "signup" ? "signup" : "signin");
	});
	eyes(root); meters(root);

	// prefill from the order page offer
	try {
		const p = JSON.parse(sessionStorage.getItem(PREFILL) || "null");
		if (p) ["name", "email", "mobile"].forEach((k) => { if (p[k] && panes.signup.elements[k]) panes.signup.elements[k].value = p[k]; });
		if (p && p.email) panes.signin.elements.email.value = p.email;
	} catch (e) { /* private mode */ }
	try { const last = localStorage.getItem("darat_login_email"); if (last && !panes.signin.elements.email.value) panes.signin.elements.email.value = last; } catch (e) { /* private mode */ }
	show(location.hash === "#signup" ? "signup" : "signin");
	requestAnimationFrame(() => root.classList.add("is-in"));  // entrance animation (CSS respects reduced motion)

	const done = async (res) => {
		try { sessionStorage.removeItem(PREFILL); } catch (e) { /* private mode */ }
		const to = (res && res.redirect) || "/account";  // the server decides: desk for staff, account for customers
		if (!to.startsWith("/app")) await syncDevice();
		location.href = to;
	};

	// ---- 6-digit code boxes (used by the code step and by the sign-in card's code mode)
	const otpBoxes = (box, onFull) => {
		const inputs = $$("[data-otp] input", box);
		const value = () => inputs.map((i) => i.value).join("");
		inputs.forEach((input, i) => {
			input.addEventListener("input", () => {
				const digits = input.value.replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d)).replace(/\D/g, "");
				if (digits.length > 1) {  // paste or email/SMS autofill into one box
					digits.slice(0, 6).split("").forEach((d, k) => { if (inputs[k]) inputs[k].value = d; });
					inputs[Math.min(digits.length, 6) - 1].focus();
				} else {
					input.value = digits;
					if (digits && inputs[i + 1]) inputs[i + 1].focus();
				}
				if (value().length === 6) onFull();
			});
			input.addEventListener("keydown", (e) => {
				if (e.key === "Backspace" && !input.value && inputs[i - 1]) { inputs[i - 1].focus(); inputs[i - 1].value = ""; }
				if (e.key === "ArrowLeft" && inputs[i - 1]) inputs[i - 1].focus();
				if (e.key === "ArrowRight" && inputs[i + 1]) inputs[i + 1].focus();
			});
		});
		return { value, clear: () => inputs.forEach((i) => (i.value = "")), focus: () => inputs[0].focus() };
	};
	// resend countdown (60 s) and the code's 10-minute bar, inside one container
	const timers = new Map();
	const startTimer = (box) => {
		const btn = $("[data-resend]", box), lbl = $("[data-countdown]", box), bar = $("[data-timer-bar]", box);
		if (bar) { bar.style.transition = "none"; bar.style.width = "100%"; void bar.offsetWidth; bar.style.transition = "width 600s linear"; bar.style.width = "0%"; }
		if (!btn) return;
		let left = 60;
		btn.disabled = true;
		clearInterval(timers.get(box));
		const tick = () => {
			lbl.textContent = left > 0 ? t(`بعد ${left} ثانية`, `in ${left}s`) : "";
			if (left-- <= 0) { clearInterval(timers.get(box)); btn.disabled = false; }
		};
		tick(); timers.set(box, setInterval(tick, 1000));
	};

	// ---- code step (sign-up, password reset, staff second step)
	const codeOtp = otpBoxes(panes.code, () => { if (flow && flow.mode !== "reset") panes.code.requestSubmit(); });
	const startCode = (mode, email, masked, data) => {
		flow = { mode, email, data };
		codeOtp.clear();
		$("[data-code-email]", panes.code).textContent = masked || email;
		$("[data-code-title]", panes.code).textContent = mode === "signup" ? t("أكّد بريدك الإلكتروني", "Verify your email")
			: mode === "reset" ? t("أعد تعيين كلمة المرور", "Reset your password")
			: mode === "staff" ? t("تحقق إضافي لدخول الفريق", "Staff sign-in: one more step") : t("أدخل رمز الدخول", "Enter your sign-in code");
		$("[data-newpw]", panes.code).hidden = mode !== "reset";
		$("[data-code-steps]", panes.code).hidden = mode !== "signup";
		$("[data-code-submit]", panes.code).textContent = mode === "reset" ? t("حفظ كلمة المرور والدخول", "Save password and sign in") : t("تأكيد", "Verify");
		showErr(panes.code, "");
		show("code");
		codeOtp.focus();
		startTimer(panes.code);
	};
	panes.code.addEventListener("submit", async (e) => {
		e.preventDefault();
		const code = codeOtp.value();
		if (code.length !== 6) return showErr(panes.code, t("أدخل الرمز المكوّن من 6 أرقام", "Enter the 6-digit code"));
		const btn = $("[data-code-submit]", panes.code);
		busy(btn, true); showErr(panes.code, "");
		try {
			let res;
			if (flow.mode === "signup") res = await api("darat.storefront.api.account_signup_verify", { email: flow.email, code, ft }, "POST");
			else res = await api("darat.storefront.api.account_code_verify", { email: flow.email, code, purpose: flow.mode, ft, redirect_to: after,
				password: flow.mode === "reset" ? panes.code.elements.new_password.value : undefined }, "POST");
			await done(res);
		} catch (ex) {
			busy(btn, false);
			showErr(panes.code, ex.message);
			codeOtp.clear(); codeOtp.focus();
		}
	});
	$("[data-resend]", panes.code).addEventListener("click", async (e) => {
		const btn = e.currentTarget;
		busy(btn, true);
		try {
			if (flow.mode === "signup") await api("darat.storefront.api.account_signup", { ...flow.data, ft }, "POST");
			else if (flow.mode === "staff") await api("darat.storefront.api.account_login", { ...flow.data, ft, redirect_to: after }, "POST");
			else await api("darat.storefront.api.account_code", { email: flow.email, purpose: flow.mode, ft }, "POST");
			toast(`${icon("mail")}<span>${t("أرسلنا رمزاً جديداً", "We sent a new code")}</span>`);
			startTimer(panes.code);
		} catch (ex) { showErr(panes.code, ex.message); }
		busy(btn, false);
		btn.disabled = true;
	});

	// ---- sign in: modes password → (code: email + "send code") → (sent: email + code boxes)
	const form = panes.signin;
	const emailOf = (f) => (f.elements.email.value || "").trim().toLowerCase();
	const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
	const validLogin = (v) => validEmail(v) || v === "administrator";  // staff "Administrator" has no email
	const signinOtp = otpBoxes($(".dx-codebox", form), () => form.requestSubmit());
	// no account with this email: say so and offer sign-up with the email filled in
	const noAcct = $("[data-noacct]", form);
	const showNoAccount = (email) => {
		$("[data-noacct-email]", noAcct).textContent = email;
		noAcct.hidden = false;
		showErr(form, "");
		$("[data-noacct-signup]", noAcct).focus();
	};
	$("[data-noacct-signup]", noAcct).addEventListener("click", () => {
		panes.signup.elements.email.value = emailOf(form);
		panes.signup.elements.email.dispatchEvent(new Event("input", { bubbles: true }));
		noAcct.hidden = true;
		show("signup");
		history.replaceState(null, "", "#signup");
		panes.signup.elements.name.focus();
	});
	form.elements.email.addEventListener("input", () => (noAcct.hidden = true));
	const setMode = (mode) => {
		form.dataset.mode = mode;
		$$("[data-only]", form).forEach((el) => (el.hidden = !el.dataset.only.split(" ").includes(mode)));
		form.elements.email.readOnly = mode === "sent";
		$("[data-signin-label]", form).textContent = mode === "code" ? t("أرسل رمز الدخول", "Send sign-in code")
			: mode === "sent" ? t("تأكيد والدخول", "Verify and sign in") : t("تسجيل الدخول", "Sign in");
		showErr(form, "");
		noAcct.hidden = true;
		if (mode === "sent") { signinOtp.clear(); signinOtp.focus(); }
		else if (mode === "code" || !form.elements.email.value) form.elements.email.focus();
		else form.elements.password.focus();
	};
	setMode("password");
	const sendLoginCode = async (btn) => {
		const email = emailOf(form);
		if (!validEmail(email)) { showErr(form, t("اكتب بريدك الإلكتروني أولاً", "Type your email first")); form.elements.email.focus(); return false; }
		busy(btn, true); showErr(form, "");
		try {
			const res = await api("darat.storefront.api.account_code", { email, purpose: "login", ft }, "POST");
			busy(btn, false);
			if (res && res.no_account) { showNoAccount(email); return false; }
			try { localStorage.setItem("darat_login_email", email); } catch (ex) { /* private mode */ }
			return true;
		} catch (ex) { busy(btn, false); showErr(form, ex.message); return false; }
	};
	form.addEventListener("submit", async (e) => {
		e.preventDefault();
		const btn = $("[data-signin-submit]", form), email = emailOf(form), mode = form.dataset.mode;
		if (mode === "code") {
			if (await sendLoginCode(btn)) { setMode("sent"); startTimer(form); }
			return;
		}
		if (mode === "sent") {
			const code = signinOtp.value();
			if (code.length !== 6) return showErr(form, t("أدخل الرمز المكوّن من 6 أرقام", "Enter the 6-digit code"));
			busy(btn, true); showErr(form, "");
			try {
				const res = await api("darat.storefront.api.account_code_verify", { email, code, purpose: "login", ft, redirect_to: after }, "POST");
				await done(res);
			} catch (ex) { busy(btn, false); showErr(form, ex.message); signinOtp.clear(); signinOtp.focus(); }
			return;
		}
		const pw = form.elements.password.value;
		if (!validLogin(email)) return showErr(form, t("أدخل بريداً إلكترونياً صحيحاً", "Enter a valid email address"));
		if (!pw) return showErr(form, t("أدخل كلمة المرور، أو اختر الدخول برمز على بريدك", "Enter your password, or choose to sign in with an email code"));
		busy(btn, true); showErr(form, "");
		try {
			const res = await api("darat.storefront.api.account_login", { email, password: pw, ft, redirect_to: after }, "POST");
			if (res && res.no_account) { busy(btn, false); return showNoAccount(email); }
			try { localStorage.setItem("darat_login_email", email); } catch (ex) { /* private mode */ }
			if (res && res.step === "code") { busy(btn, false); return startCode("staff", email, res.email, { email, password: pw }); }
			await done(res);
		} catch (ex) {
			busy(btn, false);
			showErr(form, ex.status === 401 || /credential|password|Invalid Login/i.test(ex.message)
				? t("البريد أو كلمة المرور غير صحيحة.", "The email or password is not correct.") : ex.message);
		}
	});
	$("[data-code-login]", form).addEventListener("click", () => setMode("code"));
	$("[data-use-password]", form).addEventListener("click", () => setMode("password"));
	$("[data-code-change]", form).addEventListener("click", () => setMode("code"));
	$("[data-resend]", form).addEventListener("click", async (e) => {
		const btn = e.currentTarget;
		if (await sendLoginCode(btn)) {
			toast(`${icon("mail")}<span>${t("أرسلنا رمزاً جديداً", "We sent a new code")}</span>`);
			startTimer(form);
		}
		btn.disabled = true;
	});
	$("[data-forgot]", form).addEventListener("click", async (e) => {
		const btn = e.currentTarget, email = emailOf(form);
		if (!validEmail(email)) { showErr(form, t("اكتب بريدك الإلكتروني أولاً", "Type your email first")); form.elements.email.focus(); return; }
		busy(btn, true); showErr(form, "");
		try {
			const res = await api("darat.storefront.api.account_code", { email, purpose: "reset", ft }, "POST");
			if (res && res.no_account) showNoAccount(email);
			else startCode("reset", email, res.email);
		} catch (ex) { showErr(form, ex.message); }
		busy(btn, false);
	});

	// ---- sign up
	panes.signup.addEventListener("submit", async (e) => {
		e.preventDefault();
		const form = panes.signup;
		const data = { name: form.elements.name.value.trim(), email: emailOf(form), mobile: form.elements.mobile.value.trim(),
			password: form.elements.password.value, tokens: guestTokens() };
		if (data.name.length < 2) return showErr(form, t("أدخل اسمك", "Please enter your name"));
		if (!validEmail(data.email)) return showErr(form, t("أدخل بريداً إلكترونياً صحيحاً", "Enter a valid email address"));
		if (!/^(\+?966|0)?5\d{8}$/.test(data.mobile.replace(/[\s-]/g, "").replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d))))
			return showErr(form, t("أدخل رقم جوال سعودي صحيح (05XXXXXXXX)", "Enter a valid Saudi mobile number (05XXXXXXXX)"));
		if (strength(data.password) < 2) return showErr(form, t("استخدم 8 أحرف على الأقل فيها حروف وأرقام", "Use at least 8 characters with letters and numbers"));
		const btn = form.querySelector("[type=submit]");
		busy(btn, true); showErr(form, "");
		try {
			const res = await api("darat.storefront.api.account_signup", { ...data, ft, redirect_to: after }, "POST");
			startCode("signup", data.email, res.email, data);
		} catch (ex) { showErr(form, ex.message); }
		busy(btn, false);
	});
}

// ---------------------------------------------------------------- /account hub
function initHub() {
	const root = $("[data-account]");
	if (!root) return;
	eyes(root);
	const openTab = () => { const k = location.hash.slice(1); if (k) $(`[role=tab][data-tab="${CSS.escape(k)}"]`, root)?.click(); };
	openTab();
	addEventListener("hashchange", openTab);  // header account menu links (/account#orders …) while already on the hub
	syncDevice();

	root.addEventListener("click", async (e) => {
		const re = e.target.closest("[data-reorder]");
		if (re) {
			busy(re, true);
			try {
				const res = await api("darat.storefront.api.account_reorder", { token: re.dataset.reorder }, "POST");
				res.lines.forEach((l) => cart.add(l.c, l.qty));
				location.href = "/cart";
			} catch (ex) { toast(`<span>${ex.message}</span>`); busy(re, false); }
		}
		const rm = e.target.closest("[data-addr-remove]");
		if (rm && confirm(t("حذف هذا العنوان من حسابك؟", "Remove this address from your account?"))) {
			try { await api("darat.storefront.api.account_remove_address", { id: rm.dataset.addrRemove }, "POST"); rm.closest(".ds-addr__card").remove(); }
			catch (ex) { toast(`<span>${ex.message}</span>`); }
		}
		if (e.target.closest("[data-logout-all]")) {
			try { await api("darat.storefront.api.account_logout_all", {}, "POST"); $("[data-logoutall-ok]").hidden = false; }
			catch (ex) { toast(`<span>${ex.message}</span>`); }
		}
		if (e.target.closest("[data-signout]")) {
			try { await api("logout", {}, "POST"); } catch (ex) { /* already out */ }
			location.href = "/";
		}
	});

	const form = (sel, fn) => {
		const f = $(sel, root);
		if (!f) return;
		f.addEventListener("submit", async (e) => {
			e.preventDefault();
			const btn = f.querySelector("[type=submit]");
			busy(btn, true); showErr(f, "");
			const ok = $("[data-ok]", f); if (ok) ok.hidden = true;
			try { await fn(f); if (ok) ok.hidden = false; } catch (ex) { showErr(f, ex.message); }
			busy(btn, false);
		});
	};
	form("[data-profile-form]", (f) => api("darat.storefront.api.account_profile", { name: f.elements.name.value, mobile: f.elements.mobile.value }, "POST"));
	form("[data-password-form]", async (f) => {
		await api("darat.storefront.api.account_password", { old_password: f.elements.old_password.value, new_password: f.elements.new_password.value }, "POST");
		f.reset();
	});
	form("[data-email-form]", async (f) => {
		const step2 = $("[data-email-step='2']", f);
		if (step2.hidden) {
			const res = await api("darat.storefront.api.account_email", { new_email: f.elements.new_email.value, password: f.elements.password.value }, "POST");
			$("[data-email-step='1']", f).hidden = true; step2.hidden = false;
			$("[data-email-masked]", f).textContent = res.email;
			$("[data-email-btn]", f).textContent = t("تأكيد البريد الجديد", "Confirm new email");
			f.elements.code.focus();
		} else {
			await api("darat.storefront.api.account_email_verify", { code: f.elements.code.value }, "POST");
			location.reload();
		}
	});
	const addr = $("[data-addr-new]", root);
	if (addr) {
		addr.closest("details").addEventListener("toggle", () => loadCities(addr), { once: true });
		form("[data-addr-new]", async (f) => {
			const data = Object.fromEntries(new FormData(f).entries());
			await api("darat.storefront.api.account_save_address", { address: data }, "POST");
			location.hash = "addresses"; location.reload();
		});
	}
}

// order page: "create an account" offer after a guest order (details go to the sign-up form, never the URL)
function initOffer() {
	document.addEventListener("click", (e) => {
		if (!e.target.closest("[data-signup-offer-btn]")) return;
		const box = e.target.closest("[data-signup-offer]");
		try { sessionStorage.setItem(PREFILL, box.dataset.signupOffer); } catch (ex) { /* private mode */ }
		location.href = "/account/login#signup";
	});
}

export function initAccount() {
	initAuth();
	initHub();
	initOffer();
}

// ---------------------------------------------------------------- order page: cancel / return (storefront/selfservice.py)
document.addEventListener("submit", async (e) => {
	const cancel = e.target.closest("[data-cancel-form]"), ret = e.target.closest("[data-return-form]");
	if (!cancel && !ret) return;
	e.preventDefault();
	const f = cancel || ret, btn = f.querySelector("[type=submit]");
	showErr(f, "");
	try {
		busy(btn, true);
		if (cancel) {
			const res = await api("darat.storefront.api.order_cancel", { token: f.dataset.cancelForm, reason: f.elements.reason.value, details: f.elements.details.value }, "POST");
			toast(`${icon("check-circle")}<span>${res && res.refund && res.refund.startsWith("refunded") ? t("تم إلغاء الطلب وإعادة المبلغ", "Order cancelled and refunded") : t("تم إلغاء الطلب", "Order cancelled")}</span>`);
		} else {
			const items = $$("[data-ret-item]:checked", f).map((c) => ({ c: c.value, qty: +($(`[data-ret-qty="${CSS.escape(c.value)}"]`, f)?.value || 1) }));
			if (!items.length) { busy(btn, false); return showErr(f, t("اختر قطعة واحدة على الأقل", "Choose at least one item")); }
			await api("darat.storefront.api.order_return", { token: f.dataset.returnForm, request: {
				items, reason: f.elements.reason.value, details: f.elements.details.value,
				resolution: (f.querySelector("[name=resolution]:checked") || {}).value || "Refund" } }, "POST");
			toast(`${icon("check-circle")}<span>${t("استلمنا طلب الإرجاع، وسنراسلك بالقرار", "Return request received; we'll email you")}</span>`);
		}
		setTimeout(() => location.reload(), 900);
	} catch (ex) { busy(btn, false); showErr(f, ex.message); }
});
